<template>
  <div class="booking-inline-note">
    <form v-if="editing" class="booking-inline-note__editor" @submit.prevent="save" @keydown.esc.stop.prevent="cancel">
      <textarea
        ref="input"
        v-model="draft"
        :aria-label="`Note prenotazione di ${clientName}`"
        :disabled="saving"
        rows="4"
        maxlength="2000"
        placeholder="Scrivi le note…"
        @keydown.ctrl.enter.prevent="save"
        @keydown.meta.enter.prevent="save"
      ></textarea>
      <div class="booking-inline-note__actions">
        <button type="submit" class="booking-inline-note__save" :disabled="saving || disabled">{{ saving ? 'Salvataggio…' : 'Salva' }}</button>
        <button type="button" :disabled="saving" @click="cancel">Annulla</button>
      </div>
      <span v-if="error" class="booking-inline-note__error" role="alert">{{ error }}</span>
    </form>
    <button
      v-else-if="editable"
      type="button"
      class="booking-inline-note__read"
      :disabled="disabled"
      :aria-label="`${note ? 'Modifica' : 'Aggiungi'} note per ${clientName}`"
      :title="note ? 'Modifica note' : 'Aggiungi note'"
      @click="edit"
    >
      <span :class="{ 'booking-inline-note__placeholder': !note }">{{ note || 'Aggiungi nota' }}</span>
      <span class="material-icons" aria-hidden="true">edit</span>
    </button>
    <p v-else class="booking-inline-note__text">{{ note || 'Nessuna nota' }}</p>
    <span v-if="saved && !editing" class="booking-inline-note__saved" role="status">Note salvate</span>
  </div>
</template>

<script>
import { nextTick, ref } from 'vue';

export default {
  name: 'PublicBookingNote',
  props: {
    note: { type: String, default: '' },
    clientName: { type: String, default: 'cliente' },
    editable: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    saveNote: { type: Function, required: true },
  },
  setup(props) {
    const input = ref(null);
    const editing = ref(false);
    const draft = ref('');
    const saving = ref(false);
    const error = ref('');
    const saved = ref(false);
    const edit = async () => {
      if (!props.editable || props.disabled) return;
      draft.value = props.note;
      error.value = '';
      saved.value = false;
      editing.value = true;
      await nextTick();
      input.value?.focus();
    };
    const cancel = () => {
      if (saving.value) return;
      editing.value = false;
      error.value = '';
    };
    const save = async () => {
      if (saving.value || props.disabled || !props.editable) return;
      saving.value = true;
      error.value = '';
      try {
        await props.saveNote(draft.value);
        editing.value = false;
        saved.value = true;
      } catch (reason) {
        error.value = reason.message || 'Salvataggio non riuscito. Riprova.';
      } finally {
        saving.value = false;
      }
    };
    return { input, editing, draft, saving, error, saved, edit, cancel, save };
  },
};
</script>

<style scoped>
.booking-inline-note { min-width: 0; font-size: 13px; line-height: 1.6; }
.booking-inline-note .booking-inline-note__read { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; box-sizing: border-box; width: 100%; min-width: 0; min-height: 44px; margin: -8px 0 0; padding: 8px 5px; border: 1px solid transparent; border-radius: 7px; background: transparent; color: #5c6c62; text-align: left; font: inherit; font-weight: 400; box-shadow: none; cursor: pointer; }
.booking-inline-note__read > span:first-child, .booking-inline-note__text { white-space: pre-wrap; overflow-wrap: anywhere; min-width: 0; }
.booking-inline-note__text { margin: 0; }
.booking-inline-note .booking-inline-note__read:hover:not(:disabled) { background: #f4f7f5; border-color: #dbe5df; }
.booking-inline-note .booking-inline-note__read .material-icons { flex-shrink: 0; color: #74877b; font-size: 17px; line-height: 1.5; }
.booking-inline-note__placeholder { color: #748077; }
.booking-inline-note__editor { margin: 0; padding: 0; }
.booking-inline-note textarea { box-sizing: border-box; display: block; width: 100%; min-width: 0; max-width: 100%; height: 104px; min-height: 104px; margin: 0; padding: 9px; border: 1px solid #acbfb2; border-radius: 8px; background: #fff; color: #24302b; font: inherit; resize: vertical; box-shadow: none; }
.booking-inline-note__actions { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
.booking-inline-note__actions button { flex: 1; min-height: 40px; margin: 0; padding: 7px 9px; border: 1px solid #d5ddd8; border-radius: 7px; background: #fff; color: #354a3f; font: inherit; font-size: 12px; font-weight: 650; box-shadow: none; cursor: pointer; }
.booking-inline-note__actions .booking-inline-note__save { background: #287445; border-color: #287445; color: #fff; }
.booking-inline-note button:disabled { opacity: .5; cursor: default; }
.booking-inline-note :is(button, textarea):focus-visible { outline: 2px solid #377d60; outline-offset: 2px; }
.booking-inline-note__error, .booking-inline-note__saved { display: block; margin-top: 6px; font-size: 12px; overflow-wrap: anywhere; }
.booking-inline-note__error { color: #a33d3d; }
.booking-inline-note__saved { color: #287445; }
@media (max-width: 540px) {
  .booking-inline-note textarea { font-size: 16px; }
  .booking-inline-note__actions button { min-height: 44px; }
}
</style>
